import { useQuery, useQueries, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../shared/api/client';
import { queryKeys } from '../../shared/api/query-keys';

/* ── Types ── */

export interface IntegrationApp {
  id: string;
  name: string;
  description: string | null;
  scopes: string[];
  active: boolean;
  lastUsedAt: string | null;
  createdAt: string;
}

/** Espelha `WebhookSubscriptionStatus` do dominio. */
export type WebhookStatus = 'ACTIVE' | 'PAUSED' | 'DISABLED';

/**
 * Assinatura de webhook.
 *
 * Tem `status`, nao `active`: o dominio distingue **pausado** de
 * **desativado**, e reduzir os dois a "Inativo" esconde de quem opera se a
 * entrega parou por escolha temporaria ou porque a assinatura foi encerrada.
 */
export interface Webhook {
  id: string;
  url: string;
  eventType: string;
  status: WebhookStatus;
  secret?: string | null;
  createdAt: string;
}

export interface WebhookInput {
  url: string;
  eventType: string;
}

export interface Delivery {
  id: string;
  webhookId: string;
  event: string;
  status: string;
  httpStatus: number | null;
  responseBody: string;
  createdAt: string;
}

interface Paginated<T> {
  data: T[];
  total: number;
}

interface BackendDelivery {
  id: string;
  subscriptionId: string;
  eventType: string;
  status: string;
  responseStatusCode?: number | null;
  createdAt: string;
}

interface BackendWebhook {
  id: string;
  url: string;
  eventType: string;
  status: string;
  secret?: string | null;
  createdAt: string;
}

function toWebhook(raw: BackendWebhook): Webhook {
  return {
    id: raw.id,
    url: raw.url,
    eventType: raw.eventType,
    status: raw.status as WebhookStatus,
    secret: raw.secret ?? null,
    createdAt: raw.createdAt,
  };
}

function toDelivery(raw: BackendDelivery): Delivery {
  return {
    id: raw.id,
    webhookId: raw.subscriptionId,
    event: raw.eventType,
    status: raw.status,
    httpStatus: raw.responseStatusCode ?? null,
    responseBody: '',
    createdAt: raw.createdAt,
  };
}

/**
 * Normaliza resposta de lista: o backend retorna envelope paginado
 * `{data, ...}` (todo19); aceita array direto por tolerância.
 */
function asArray<T>(res: T[] | Paginated<T> | null | undefined): T[] {
  if (!res) return [];
  if (Array.isArray(res)) return res;
  return res.data ?? [];
}

/**
 * Exportacao, como a tela a usa.
 *
 * Nao tem `filters` nem `fileUrl`: nenhum dos dois existe em
 * `ExportResponseDto`. `filters` era preenchido com `{}` fixo pelo mapeador, e
 * a coluna "Filtros" mostrava "-" em toda linha desde sempre. `fileUrl`
 * recebia `storagePath` e nunca era lido — o download vai por
 * `GET /integration/exports/:id/download`, que devolve o arquivo.
 */
export interface Export {
  id: string;
  type: string;
  format: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  fileName: string;
  totalRecords: number;
  fileHashSha256: string | null;
  completedAt: string | null;
  createdAt: string;
}

/** Espelha `ExportResponseDto`, campo a campo. */
interface BackendExport {
  id: string;
  type: string;
  format: string;
  status: string;
  fileName: string;
  storagePath?: string | null;
  totalRecords: number;
  fileHashSha256?: string | null;
  manifest?: { fileName: string; hashSha256: string; size: number }[];
  completedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

function toExport(raw: BackendExport): Export {
  return {
    id: raw.id,
    type: raw.type,
    format: raw.format,
    status: raw.status.toLowerCase() as Export['status'],
    fileName: raw.fileName,
    totalRecords: raw.totalRecords,
    fileHashSha256: raw.fileHashSha256 ?? null,
    completedAt: raw.completedAt ?? null,
    createdAt: raw.createdAt,
  };
}

export interface Import {
  id: string;
  fileName: string;
  totalRows: number;
  type: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'uploaded' | 'validating' | 'validated';
  errors: string[];
  createdAt: string;
}

interface BackendImportError {
  row: number;
  column?: string | null;
  message: string;
}

interface BackendImport {
  id: string;
  fileName: string;
  totalRows?: number | null;
  type: string;
  status: string;
  errors?: BackendImportError[] | null;
  createdAt: string;
}

function toImport(raw: BackendImport): Import {
  return {
    id: raw.id,
    fileName: raw.fileName,
    totalRows: raw.totalRows ?? 0,
    type: raw.type,
    status: raw.status.toLowerCase() as Import['status'],
    errors: (raw.errors ?? []).map((e) =>
      `Linha ${e.row}${e.column ? ` [${e.column}]` : ''}: ${e.message}`,
    ),
    createdAt: raw.createdAt,
  };
}

export interface ActivityEvent {
  id: string;
  kind: 'export' | 'import';
  description: string;
  status: string;
  createdAt: string;
}

export interface IntegrationOverview {
  apps: IntegrationApp[];
  webhooks: Webhook[];
  exports: Export[];
  imports: Import[];
  recentActivity: ActivityEvent[];
}

/* ── Overview Hook ── */

export function useIntegrationOverview() {
  return useQueries({
    queries: [
      {
        queryKey: ['integration', 'apps'],
        /* Backend devolve envelope paginado; a tela consome array. */
        queryFn: async () =>
          (await apiClient.get<{ data: IntegrationApp[] }>('/integration/applications'))
            .data,
        staleTime: 30_000,
      },
      {
        queryKey: queryKeys.integration.webhooks(),
        queryFn: async (): Promise<Webhook[]> => {
          const res = await apiClient.get<BackendWebhook[] | Paginated<BackendWebhook>>(
            '/integration/webhooks',
          );
          return asArray(res).map(toWebhook);
        },
        staleTime: 30_000,
      },
      {
        queryKey: queryKeys.integration.exports(),
        queryFn: async (): Promise<Export[]> => {
          const res = await apiClient.get<BackendExport[] | Paginated<BackendExport>>(
            '/integration/exports',
          );
          return asArray(res).map(toExport);
        },
        staleTime: 30_000,
      },
      {
        queryKey: queryKeys.integration.imports(),
        queryFn: async (): Promise<Import[]> => {
          const res = await apiClient.get<BackendImport[] | Paginated<BackendImport>>(
            '/integration/imports',
          );
          return asArray(res).map(toImport);
        },
        staleTime: 30_000,
      },
    ],
    combine: (results) => {
      const [appsRes, webhooksRes, exportsRes, importsRes] = results;

      const apps = appsRes.data ?? [];
      const webhooks = webhooksRes.data ?? [];
      const exportsData = exportsRes.data ?? [];
      const importsData = importsRes.data ?? [];

      const allEvents: ActivityEvent[] = [
        ...exportsData.map((exp) => ({
          id: exp.id,
          kind: 'export' as const,
          description: `${exp.type} — ${exp.format.toUpperCase()}`,
          status: exp.status,
          createdAt: exp.createdAt,
        })),
        ...importsData.map((imp) => ({
          id: imp.id,
          kind: 'import' as const,
          description: imp.fileName,
          status: imp.status,
          createdAt: imp.createdAt,
        })),
      ];

      allEvents.sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      );

      const recentActivity = allEvents.slice(0, 5);

      return {
        data: {
          apps,
          webhooks,
          exports: exportsData,
          imports: importsData,
          recentActivity,
        } as IntegrationOverview,
        isLoading: results.some((r) => r.isLoading),
        isError: results.some((r) => r.isError),
        errors: results.map((r) => r.error).filter(Boolean),
      };
    },
  });
}

/* ── Webhook Hooks ── */

export function useWebhooks() {
  return useQuery({
    queryKey: queryKeys.integration.webhooks(),
    queryFn: async (): Promise<Webhook[]> => {
      const res = await apiClient.get<BackendWebhook[] | Paginated<BackendWebhook>>(
        '/integration/webhooks',
      );
      return asArray(res).map(toWebhook);
    },
  });
}

export function useWebhookDeliveries(webhookId: string) {
  return useQuery({
    queryKey: [...queryKeys.integration.webhooks(), webhookId, 'deliveries'],
    queryFn: async (): Promise<Delivery[]> => {
      const res = await apiClient.get<BackendDelivery[] | Paginated<BackendDelivery>>(
        `/integration/webhooks/${webhookId}/deliveries`,
      );
      return asArray(res).map(toDelivery);
    },
    enabled: !!webhookId,
  });
}

export function useCreateWebhook() {
  const qc = useQueryClient();
  return useMutation({
    /* A resposta do POST e o mesmo DTO da listagem: passa pelo mapeador. */
    mutationFn: async (input: WebhookInput): Promise<Webhook> =>
      toWebhook(await apiClient.post<BackendWebhook>('/integration/webhooks', input)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.integration.webhooks() });
    },
  });
}

export function useDeleteWebhook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiClient.delete<void>(`/integration/webhooks/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.integration.webhooks() });
    },
  });
}

/* ── Export Hooks ── */

export function useExports() {
  return useQuery({
    queryKey: queryKeys.integration.exports(),
    queryFn: async (): Promise<Export[]> => {
      const res = await apiClient.get<BackendExport[] | Paginated<BackendExport>>(
        '/integration/exports',
      );
      return asArray(res).map(toExport);
    },
  });
}

export function useDownloadExport() {
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await fetch(`/api/v1/integration/exports/${id}/download`, {
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Falha ao baixar arquivo');
      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition') ?? '';
      const match = disposition.match(/filename="?(.+?)"?$/);
      const filename = match?.[1] ?? `export-${id}.csv`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
  });
}

export function useProcessExport() {
  const qc = useQueryClient();
  return useMutation({
    /* A resposta do POST e o mesmo DTO da listagem: passa pelo mapeador, em
       vez de tipar `Export` direto e herdar campo que o backend nao manda. */
    mutationFn: async (id: string): Promise<Export> =>
      toExport(await apiClient.post<BackendExport>(`/integration/exports/${id}/process`, {})),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.integration.exports() });
    },
  });
}

/* ── Import Hooks ── */

export function useImports() {
  return useQuery({
    queryKey: queryKeys.integration.imports(),
    queryFn: async (): Promise<Import[]> => {
      const res = await apiClient.get<BackendImport[] | Paginated<BackendImport>>(
        '/integration/imports',
      );
      return asArray(res).map(toImport);
    },
  });
}

export function useUploadImport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch('/api/v1/integration/imports', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error((err as { message?: string }).message ?? 'Falha ao importar');
      }
      return response.json() as Promise<Import>;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.integration.imports() });
    },
  });
}

export function useProcessImport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiClient.post<Import>(`/integration/imports/${id}/process`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.integration.imports() });
    },
  });
}
